import { motion } from 'motion/react';
import { Lock, FileText, Download, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { geminiService } from '../services/geminiService';
import ContentModal from '../components/ContentModal';
import BriefingRequestModal from '../components/BriefingRequestModal';

const vaultItems = [
  {
    title: "The Elite Vetting Rubric",
    type: "Evaluation Matrix",
    security: "Level 1",
    desc: "A clinical 50-point checklist for auditing a partner's character, capital, and consistency before commitment."
  },
  {
    title: "The Walk-Away Protocol",
    type: "Exit Strategy",
    security: "Level 2",
    desc: "Step-by-step logistical and psychological planning for high-stakes relationship dissolution."
  },
  {
    title: "Executive Presence Masterclass",
    type: "Private Video Essay",
    security: "Level 1",
    desc: "An unlisted video briefing on mastering the non-verbal architectural cues of high-status women."
  },
  {
    title: "Financial Autonomy Workbook",
    type: "Excel Framework",
    security: "Level 2",
    desc: "Identifying structural drains on your private capital and enforcing financial boundaries."
  }
];

export default function ResourceVault() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState({ title: '', type: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  const handleOpenResource = async (title: string, type: string) => {
    setModalOpen(true);
    setLoading(true);
    setSelectedContent({ title, type, content: '' });
    
    try {
      const content = await geminiService.generateResource(title, type);
      setSelectedContent(prev => ({ ...prev, content }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!isUnlocked) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center bg-theme-bg px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md w-full p-12 border border-gold/20 bg-gold/5 text-center"
          >
              <div className="w-16 h-16 bg-gold/10 flex items-center justify-center mx-auto mb-8 border border-gold/30">
                <Lock className="h-8 w-8 text-gold" />
              </div>
              <h1 className="text-2xl font-serif italic mb-4">Secure Authorization</h1>
              <p className="text-xs text-taupe mb-8 uppercase tracking-widest leading-loose">
                Access to the VIP Vault requires high-level biometric or PIN authentication.
              </p>
              <div className="space-y-4">
                  <input
                    type="password"
                    placeholder="Enter Private PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border px-4 py-3 text-center tracking-[1em] text-theme-text focus:outline-none focus:border-gold/50"
                  />
                  <button
                    onClick={() => setIsUnlocked(true)}
                    className="btn-gold w-full"
                  >
                    Authorize Access
                  </button>
              </div>
              <p className="mt-8 text-[9px] text-taupe/60 italic">
                Unauthorized access attempts are logged and reported to security.
              </p>
          </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 bg-theme-bg text-theme-text">
      <BriefingRequestModal 
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        requestType="Bespoke Strategic Roadmap"
      />
      <ContentModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedContent.title}
        category={selectedContent.type}
        content={selectedContent.content}
        isLoading={loading}
      />
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div>
            <span className="kicker">Secure VIP Vault</span>
            <h1>Exclusive Resources</h1>
            <p className="text-taupe text-sm italic">Confidential templates and blueprints for structural mastery.</p>
          </div>
          <button
            onClick={() => setIsUnlocked(false)}
            className="flex items-center space-x-2 text-[10px] uppercase tracking-widest text-theme-text/50 hover:text-gold transition-all"
          >
            <Lock className="h-4 w-4" />
            <span>Lock Vault</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {vaultItems.map((item, i) => (
                <motion.div
                    key={i}
                    whileHover={{ y: -5 }}
                    className="flex flex-col p-8 border border-theme-border bg-theme-bg hover:border-gold/30 transition-all group"
                >
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 border border-theme-border flex items-center justify-center text-taupe group-hover:text-gold transition-colors">
                            <FileText className="h-6 w-6" />
                        </div>
                        <span className="text-[9px] px-2 py-1 border border-gold/30 text-gold uppercase tracking-[0.2em]">
                            {item.security}
                        </span>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-taupe mb-2">{item.type}</span>
                    <h3 className="text-xl font-serif italic mb-4">{item.title}</h3>
                    <p className="text-sm text-taupe leading-relaxed mb-8 italic">{item.desc}</p>
                    <div className="mt-auto flex items-center justify-between pt-6 border-t border-theme-border">
                        <button 
                          onClick={() => handleOpenResource(item.title, item.type)}
                          className="flex items-center space-x-2 text-[10px] uppercase tracking-widest text-gold hover:text-theme-text transition-all"
                        >
                            <Download className="h-4 w-4" />
                            <span>Download Securely</span>
                        </button>
                        <button 
                          onClick={() => handleOpenResource(item.title, item.type)}
                          className="text-taupe hover:text-theme-text transition-all"
                        >
                            <Eye className="h-4 w-4" />
                        </button>
                    </div>
                </motion.div>
            ))}
        </div>

        <div className="mt-24 p-12 border border-theme-border text-center">
            <h2 className="text-2xl font-serif italic mb-4">Request a Private Briefing</h2>
            <p className="text-sm text-taupe mb-8 italic">Require a bespoke psychological audit or strategic roadmap for a unique situation?</p>
            <button 
              onClick={() => setRequestModalOpen(true)}
              className="btn-outline"
            >Book Executive Consultation</button>
        </div>
      </div>
    </div>
  );
}
